/**
 * Export filtered data as JSON or CSV, and trigger browser download.
 */

import { isMissing } from './flatten';
import type { ColumnDef, DownloadText, Row, ToCsv, ToJson } from './types';

const UTF8_BOM = '﻿';

function escapeCSVField(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function formatCSVCell(value: unknown): string {
  if (isMissing(value)) return '';
  if (typeof value === 'string') return escapeCSVField(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return escapeCSVField(JSON.stringify(value));
}

export const toJson: ToJson = (rows, indices) => {
  return JSON.stringify(
    indices.map((i) => rows[i]),
    null,
    2
  );
};

export const toCsv: ToCsv = (rows, indices, columns) => {
  const lines: string[] = [];
  lines.push(columns.map((c) => escapeCSVField(c.id)).join(','));

  for (const i of indices) {
    const row = rows[i];
    const cells = columns.map((col) => {
      const value = row[col.path[0]];
      let current: unknown = value;
      for (let j = 1; j < col.path.length; j++) {
        if (current !== null && typeof current === 'object' && !Array.isArray(current)) {
          current = (current as Record<string, unknown>)[col.path[j]];
        } else {
          current = undefined;
          break;
        }
      }
      return formatCSVCell(current);
    });
    lines.push(cells.join(','));
  }

  return UTF8_BOM + lines.join('\r\n');
};

export const downloadText: DownloadText = (filename, text, mime) => {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    URL.revokeObjectURL(url);
  }
};
